import { Loader2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { userAdminPath } from '@/platform/lib/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useAddTeamMember,
  useRemoveTeamMember,
  useTeamMembers,
} from '@/platform/features/organisation/use-organisation';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { adminTableFrameClassName } from '@/platform/features/admin-panel';

type AgentOption = { id: string; displayName: string; email?: string };

type TeamMembersDialogProps = {
  open: boolean;
  teamId: string | null;
  teamName: string;
  canWrite: boolean;
  agents: AgentOption[];
  onOpenChange: (open: boolean) => void;
};

export function TeamMembersDialog({
  open,
  teamId,
  teamName,
  canWrite,
  agents,
  onOpenChange,
}: TeamMembersDialogProps) {
  const members = useTeamMembers(open ? (teamId ?? undefined) : undefined);
  const addMember = useAddTeamMember(teamId ?? '');
  const removeMember = useRemoveTeamMember(teamId ?? '');
  const [selectedUserId, setSelectedUserId] = useState('');

  const memberIds = useMemo(
    () => new Set((members.data?.items ?? []).map((item) => item.id)),
    [members.data?.items],
  );

  const assignable = useMemo(
    () => agents.filter((agent) => !memberIds.has(agent.id)),
    [agents, memberIds],
  );

  async function onAssign() {
    if (!teamId || !selectedUserId) {
      return;
    }
    try {
      await addMember.mutateAsync({ userId: selectedUserId });
      toast.success('Mitglied zugewiesen.');
      setSelectedUserId('');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Mitglied konnte nicht zugewiesen werden.',
      );
    }
  }

  async function onRemove(userId: string, displayName: string, isLead: boolean) {
    if (!teamId) {
      return;
    }
    try {
      await removeMember.mutateAsync(userId);
      toast.success(
        isLead
          ? `„${displayName}“ entfernt; Teamleitung wurde freigegeben.`
          : `„${displayName}“ aus dem Team entfernt.`,
      );
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Mitglied konnte nicht entfernt werden.',
      );
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setSelectedUserId('');
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Mitglieder — {teamName}</DialogTitle>
          <DialogDescription>
            Mitgliedschaft setzt die Team-Zuordnung des Benutzers. Eine Person kann nur einem Team
            angehören. Wer Leitung eines anderen Teams ist, kann hier nicht zugewiesen werden.
          </DialogDescription>
        </DialogHeader>

        {members.isPending && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Mitglieder werden geladen …
          </p>
        )}

        {members.isError && (
          <p role="alert" className="text-sm text-destructive">
            {members.error instanceof ApiRequestError
              ? members.error.message
              : 'Mitglieder konnten nicht geladen werden.'}
          </p>
        )}

        {members.data && (
          <div className={adminTableFrameClassName}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  {canWrite && (
                    <TableHead className="w-28">
                      <span className="sr-only">Aktionen</span>
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.data.items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 3 : 2}
                      className="text-center text-muted-foreground"
                    >
                      Noch keine Mitglieder.
                    </TableCell>
                  </TableRow>
                ) : (
                  members.data.items.map((member) => (
                    <TableRow key={member.id} className={cn(!member.isActive && 'opacity-70')}>
                      <TableCell>
                        <Link
                          to={userAdminPath(member.id)}
                          className="font-medium underline-offset-4 hover:underline"
                        >
                          {member.displayName}
                        </Link>
                        {member.isLead && (
                          <Badge variant="secondary" className="ml-2">
                            Leitung
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {member.email}
                      </TableCell>
                      {canWrite && (
                        <TableCell>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={removeMember.isPending}
                            onClick={() =>
                              void onRemove(member.id, member.displayName, member.isLead)
                            }
                          >
                            Entfernen
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {canWrite && (
          <div className="space-y-2 border-t pt-3">
            <Label htmlFor="team-member-add">Mitglied zuweisen</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Select
                value={selectedUserId || undefined}
                onValueChange={setSelectedUserId}
                disabled={assignable.length === 0}
              >
                <SelectTrigger id="team-member-add" className="w-full">
                  <SelectValue
                    placeholder={
                      assignable.length === 0
                        ? 'Keine weiteren Agenten verfügbar'
                        : 'Person wählen …'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {assignable.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.displayName}
                      {agent.email ? ` (${agent.email})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                disabled={!selectedUserId || addMember.isPending || assignable.length === 0}
                onClick={() => void onAssign()}
              >
                {addMember.isPending ? (
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                ) : null}
                Zuweisen
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
